import FormGroupTile from "../../basic/FormGroupTile";
import { useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../../utils/marketplaceConstants";
import AdPlanChoiceCard from "./AdPlanChoiceCard";
import Skeleton from "react-loading-skeleton";
import { ProductSelector } from "../ProductSelector/ProductSelector";
import { KeywordSelector } from "../../../custom/KeywordSelector";

import { Col, Row, FormLabel, Form } from "react-bootstrap";
import {
  AD_TYPES,
  VIDEO_AD_TYPES,
  GOALS,
  AD_STATES,
  TARGET_TYPES,
  WALMART_SB_MIN_BID_DEFAULT,
  SD_CAMPAIGN_TYPE_OPTIONS,
  SB_BID_STRATEGY_OPTIONS,
  MODERATION_STATUSES,
  WALMART_SBV_MIN_BID_DEFAULT,
  SMB_STARTER_ALLOWED_AD_TYPES,
  WALMART_NEGATIVE_KEYWORD_AD_TYPES,
} from "./constants";
import ProductImage from "../../../custom/category/listings/ProductImage";
import { toCurrency } from "../../../../utils/marketplaceConstants";
import { useFetch } from "../../../../hooks/api";
import AssetLabel from "../AssetSelector/AssetLabel";
import CustomHref from "../CustomHref";
import { Link } from "@tanstack/react-router";
import { getCampaignsByAdType } from "../../../../utils/getCampaignsByAdType";
import { Button } from "../../basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../utils/api";
import { css } from "../../../../../styled-system/css";
import { successToast, errorToast } from "../../../../utils/toasts";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { token } from "../../../../../styled-system/tokens";
import { Checkbox } from "../../../fields/base/Checkbox";
import { useAppSelector } from "../../../../redux/hooks";
import { useGetStorePageProducts } from "./useGetStorePageProducts";
import { HoverInfo } from "../../basic/Tooltip";
import { AsinListInput, KeywordListInput } from "../../../fields/base/StringListInput.presets";
import TagsReviewDisplay from "../TagsReviewDisplay";
import { StorePageProductSelector } from "./StorePageProductSelector";

const ControlLabel = Form.Label;

export const FieldWrapper = ({ children, displayError, styles = {} }) => (
  <div style={{ marginBottom: displayError ? "0.5em" : "0", ...styles }}>{children}</div>
);

export const StepTitle = ({ children, overrideStyles }) => (
  <div style={{ textAlign: "left", marginBottom: "2rem", ...overrideStyles }}>
    <h6
      className="title"
      style={{
        textTransform: "none",
        color: "#484848",
        fontSize: "20px",
        fontWeight: "700",
      }}
    >
      {children}
    </h6>
    <hr />
  </div>
);

export const NameField = ({ state, onChange, errors, fieldsToValidate, isDisabled }) => {
  const { t } = useTranslation(["advertising"]);
  return (
    <FieldWrapper displayError={Object.keys(errors)?.includes("name") && fieldsToValidate?.includes("name")}>
      <div style={{ maxWidth: "70%" }}>
        <FormGroupTile
          formControlClass="text-start"
          type="formcontrol"
          label={undefined}
          formControlType={"text"}
          stateName={"name"}
          handleChildFormElement={(key, value) => onChange({ [key]: value })}
          placeholder={t(($) => $.advertising["Plan Name"])}
          defaultValue={state.name}
          autoFocus={true}
          style={{ border: "1px solid #BBB" }}
          disabled={isDisabled}
        />
        {errors.name && fieldsToValidate?.includes("name") && (
          <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
            {errors.name}
          </small>
        )}
      </div>
    </FieldWrapper>
  );
};

export const CategoryField = ({
  categories = [],
  marketPlace,
  channel,
  dashboard = false,
  state = {},
  errors,
  onChange,
  fieldsToValidate,
  inline,
  existingAdCategorySelector,
  review,
  showAllCategoriesOption = false,
  labelText = null,
  isModal = false,
  additionalProductSelectionMode = false,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [search, setSearch] = useState(null);

  const { data: categoryData } = useFetch(
    ["categories"],
    "/api/gvads/categories",
    {
      marketplace: marketPlace.marketPlace,
      channel: channel === "amazon" ? "AMZ" : channel,
    },
    {
      default: [],
      select: (res) => res.data,
    },
  );

  const leafCategoryOptions = useMemo(
    () =>
      categoryData
        ?.sort((a, b) => (a.leaf_category_name > b.leaf_category_name ? 1 : -1))
        ?.filter((c) => (search ? c.leaf_category_name?.toLowerCase().indexOf(search.toLowerCase()) > -1 : c))
        ?.map((category) => ({
          label: (
            <>
              {category.leaf_category_name}{" "}
              <small>
                (
                {t(($) => $.advertising["{count, plural, one {# Product} other {# Products}}"], {
                  count: category.product_count,
                })}
                )
              </small>
            </>
          ),
          value: category.id,
        })),
    [search, categoryData, t],
  );
  const categoryOptions =
    showAllCategoriesOption && leafCategoryOptions ?
      [{ label: t(($) => $.advertising["All Categories"]), value: null }, ...leafCategoryOptions]
    : leafCategoryOptions;

  if (
    (((state.newLandingPage || state.newProductCollection) && state.adType === AD_TYPES.SPONSORED_BRAND.value) ||
      (state.id && !isModal)) &&
    !existingAdCategorySelector
  ) {
    return null;
  }

  return (
    <div
      style={{
        maxWidth: existingAdCategorySelector ? "350px" : "90%",
        width: existingAdCategorySelector ? "350px" : "inherit",
      }}
    >
      {!inline && !existingAdCategorySelector && (
        <span
          style={{
            fontSize: "20px",
            fontWeight: 500,
            margin: "1rem 0 0",
            paddingBottom: ".5rem",
          }}
        >
          {labelText ? labelText : t(($) => $.advertising["Category"])}
        </span>
      )}
      <FieldWrapper
        displayError={Object.keys(errors).includes("categoryId") && fieldsToValidate?.includes("categoryId")}
        styles={{
          marginTop: state.id || review ? 0 : "1rem",
        }}
      >
        <FormGroupTile
          type="select"
          search={search}
          setSearch={setSearch}
          multi={false}
          stateName={"categoryId"}
          handleChildFormElement={(key, value) => {
            // In update panel, changing category removes all ad products from rendering in selector
            if (state.id && !additionalProductSelectionMode) {
              onChange({ updatedCategoryId: value.value });
            } else {
              onChange({ categoryId: value.value });
            }
          }}
          label={undefined}
          defaultValue={
            state.updatedCategoryId ?
              categoryOptions?.find((c) => c.value === state.updatedCategoryId)
            : categoryOptions?.find((c) => c.value === (state.categoryId == null ? null : parseInt(state.categoryId)))
          }
          options={categoryData?.length ? categoryOptions : categories?.sort((a, b) => (a.label > b.label ? 1 : -1))}
          filterOption={() =>
            categoryData?.length ? categoryOptions : categories?.sort((a, b) => (a.label > b.label ? 1 : -1))
          }
          disabled={!dashboard || isDisabled}
        />
        {errors.categoryId && fieldsToValidate?.includes("categoryId") && (
          <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
            {errors.categoryId}
          </small>
        )}
      </FieldWrapper>
    </div>
  );
};

export const AcosGoalsField = ({
  state,
  onChange,
  marketPlace,
  isChild = false,
  errors,
  parentDailyBudget,
  roas,
  hideLabels,
  childIndex,
  savedChildPlans,
  setSavedChildPlans,
  allowChildEdit,
  isDisabled = false,
}) => {
  const { t } = useTranslation(["advertising"]);
  if (state.canHaveChildren) {
    return null;
  }

  return (
    <>
      <Row
        style={{
          paddingTop: 0,
        }}
      >
        <div className="d-flex">
          <label
            className="m-1 py-2"
            style={{
              color: "#73706E",
              fontWeight: "700",
              textTransform: "none",
              width: isChild ? "120px" : "200px",
            }}
          >
            {isChild ?
              t(($) => $.advertising["Low"])
            : roas ?
              t(($) => $.advertising["Optimal ROAS Range"])
            : t(($) => $.advertising["Optimal ACOS Range"])}
          </label>
          {isChild && (
            <label
              className="m-1 py-2"
              style={{
                color: "#73706E",
                fontWeight: "700",
                textTransform: "none",
              }}
            >
              {t(($) => $.advertising["High"])}
            </label>
          )}
        </div>

        <Col xs={{ span: 12, order: roas ? 2 : 1 }} lg={{ span: 4, order: roas ? 2 : 1 }} className="px-3">
          <FormGroupTile
            type="formcontrol"
            label={
              hideLabels ? undefined
              : roas ?
                t(($) => $.advertising["High"])
              : t(($) => $.advertising["Low"])
            }
            formControlType={"number"}
            prefix={roas ? undefined : "%"}
            prefixSide={"right"}
            toolTip={
              hideLabels ? undefined
              : roas ?
                t(
                  ($) =>
                    $.advertising["Short term ad spend as a rating of sales to enable successful campaign launch."],
                )
              : t(
                  ($) =>
                    $.advertising[
                      "ACoS is a metric that measures how much you spend on an advertisement for every dollar of revenue you make. A target ACoS is the percentage where you hit your target profit margin, and your ad is bringing in additional revenue."
                    ],
                )

            }
            handleChildFormElement={(key, value) => {
              if (childIndex !== undefined) {
                const tempChildren = savedChildPlans;
                tempChildren[childIndex][key] = value;
                setSavedChildPlans(tempChildren);
                onChange({ children: tempChildren });
                return;
              }
              onChange({ [key]: value });
            }}
            stateName={"targetACOS"}
            placeholder={
              roas ? t(($) => $.advertising["Enter Target Roas"]) : t(($) => $.advertising["Enter Target AcoS"])
            }
            defaultValue={state.targetACOS}
            disabled={(state.id && isChild && !allowChildEdit) || isDisabled}
          />
          {errors.targetACOS && <small className="text-danger">{errors.targetACOS}</small>}
        </Col>
        <Col xs={{ span: 12, order: roas ? 1 : 2 }} lg={{ span: 4, order: roas ? 1 : 2 }} className="px-3">
          <FormGroupTile
            type="formcontrol"
            label={
              hideLabels ? undefined
              : roas ?
                t(($) => $.advertising["Low"])
              : t(($) => $.advertising["High"])
            }
            prefix={roas ? undefined : "%"}
            prefixSide={"right"}
            formControlType={"number"}
            toolTip={
              hideLabels ? undefined
              : roas ?
                t(
                  ($) =>
                    $.advertising["Short term ad spend as a rating of sales to enable successful campaign launch."],
                )
              : t(
                  ($) =>
                    $.advertising[
                      "ACoS is a metric that measures how much you spend on an advertisement for every dollar of revenue you make. The max ACoS is the maximum amount of money you want to spend on an advertisement per dollar of revenue made."
                    ],
                )

            }
            stateName={"maxACOS"}
            handleChildFormElement={(key, value) => {
              if (childIndex !== undefined) {
                const tempChildren = savedChildPlans;
                tempChildren[childIndex][key] = value;
                setSavedChildPlans(tempChildren);
                onChange({ children: tempChildren });
                return;
              }
              onChange({ [key]: value });
            }}
            placeholder={roas ? t(($) => $.advertising["Enter Max ROAS"]) : t(($) => $.advertising["Enter Max ACoS"])}
            defaultValue={state.maxACOS}
            disabled={(state.id && isChild && !allowChildEdit) || isDisabled}
          />
          {errors.maxACOS && <small className="text-danger">{errors.maxACOS}</small>}
        </Col>
        <Col xs={{ span: 12, order: 3 }} lg={{ span: 4, order: 3 }} className="px-3">
          {isChild ?
            <>
              <FormGroupTile
                type="formcontrol"
                label={hideLabels ? undefined : t(($) => $.advertising["Budget Allocation (%)"])}
                formControlType={"number"}
                prefix={"%"}
                prefixSide={"right"}
                stateName={"allocation"}
                handleChildFormElement={(key, value) => {
                  if (childIndex !== undefined) {
                    const tempChildren = savedChildPlans;
                    tempChildren[childIndex][key] = value;
                    setSavedChildPlans(tempChildren);
                    onChange({ children: tempChildren });
                    return;
                  }
                  onChange({
                    [key]: value,
                    parentDailyBudget,
                  });
                }}
                placeholder={t(($) => $.advertising["Allocation (%)"])}
                defaultValue={state.allocation}
                disabled={(state.id && isChild && !allowChildEdit) || isDisabled}
              />
              {parentDailyBudget && (
                <div className="text-center mt-3" style={{ marginRight: "18px" }}>
                  {toCurrency((parentDailyBudget / 100.0) * state.allocation, marketPlace.marketPlace)}
                </div>
              )}
            </>
          : <>
              <FormGroupTile
                type="formcontrol"
                label={t(($) => $.advertising["Daily Budget"])}
                formControlType={"number"}
                prefix={MARKETPLACE_CURRENCY_DENOMINATION[marketPlace.marketPlace] ?? "$"}
                toolTip={t(
                  ($) => $.advertising["The daily maximum amount you want to spend on advertising for this plan."],
                )}
                stateName={"dailyBudget"}
                handleChildFormElement={(key, value) => {
                  onChange({ [key]: value });
                }}
                placeholder={t(($) => $.advertising["Daily Budget"])}
                defaultValue={state.dailyBudget}
                disabled={isDisabled}
              />
              {errors.dailyBudget && (
                <p className="text-danger pt-2" style={{ marginLeft: "2rem", fontSize: "12px" }}>
                  {errors.dailyBudget}
                </p>
              )}
            </>
          }
        </Col>
      </Row>
      {!allowChildEdit && isChild && (
        <Row>
          <Col xs={12} className="pt-3">
            <p style={{ fontSize: "1.4rem" }}>
              <Trans
                t={t}
                i18nKey={($) =>
                  $.advertising[
                    "To edit these fields, please <summaryTableLink>visit the summary table</summaryTableLink>"
                  ]
                }
                components={{
                  summaryTableLink: (
                    <Link
                      to="/user/advertising/dashboard/ads/$id"
                      params={{ id: String(state.parent) }}
                      style={{
                        color: "#d91266",
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                    />
                  ),
                }}
              />
            </p>
          </Col>
        </Row>
      )}
      {errors.allocation && <h6 className="text-danger">{errors.allocation}</h6>}
    </>
  );
};

export const CompositeBudgetStatusField = ({ state, onChange, isDisabled }) => {
  const { t } = useTranslation(["advertising"]);
  if (!state.canHaveChildren || !state.id) {
    return null;
  }
  const stateOptions = [AD_STATES.PAUSE, AD_STATES.ACTIVE, AD_STATES.ARCHIVE, AD_STATES.ENDED].map((o) => ({
    ...o,
    label: t(o.label),
  }));
  return (
    <FieldWrapper>
      <Row>
        <Col xs={12} lg={4}>
          <FormGroupTile
            type="select"
            stateName={"advStatus"}
            toolTip={t(($) => $.advertising["Enable or Pause advertising of the current product."])}
            handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
            label={t(($) => $.advertising["Advertising State"])}
            defaultValue={stateOptions.find((o) => o.value === state.advStatus)}
            options={stateOptions}
            disabled={isDisabled}
          />
        </Col>
      </Row>
    </FieldWrapper>
  );
};

export const TargetingField = ({ state, onChange, errors, fieldsToValidate, channel, isSummaryCard, review }) => {
  const { t } = useTranslation(["advertising"]);
  let targetingOptions =
    channel === "walmart" ?
      state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value ? [TARGET_TYPES.AUTOMATIC]
      : [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) ?
        [TARGET_TYPES.MANUAL]
      : [
          TARGET_TYPES.MANUAL,
          {
            ...TARGET_TYPES.FUNNEL,
            description: ($) =>
              $.advertising[
                "Provide descriptions of your product attributes and automatically generate a list of keyword targets."
              ],
          },
        ]
    : state.id ? [TARGET_TYPES.FUNNEL, TARGET_TYPES.MANUAL, TARGET_TYPES.AUTOMATIC]
    : [TARGET_TYPES.FUNNEL, TARGET_TYPES.MANUAL];

  if ([AD_TYPES.SPONSORED_BRAND_VIDEO.value, AD_TYPES.SPONSORED_BRAND.value].includes(state.adType) && !state.id) {
    targetingOptions = [TARGET_TYPES.MANUAL, TARGET_TYPES.FUNNEL];
  }
  if ([AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(state.adType)) {
    targetingOptions = [TARGET_TYPES.AUTOMATIC];
  }

  const summaryCardContent = targetingOptions.find((choice) => choice.value === state.targetType);

  const targetingTypeTBD =
    state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value && (state.targetType === "N/A" || !state.targetType);

  if ((!targetingTypeTBD && !isSummaryCard) || state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    return null;
  }

  return (
    <FieldWrapper>
      <Row style={{ marginTop: "2rem" }}>
        <Col xs={12}>
          {!isSummaryCard && (
            <>
              <h2 style={{ fontWeight: 500, marginTop: 0, fontSize: "20px" }}>
                {t(($) => $.advertising["Targeting Strategy"])}
              </h2>
              {errors.targetType && fieldsToValidate?.includes("targetType") && (
                <small className="text-danger">{errors.targetType}</small>
              )}
            </>
          )}
          {isSummaryCard && !targetingTypeTBD ?
            <AdPlanChoiceCard
              label={summaryCardContent?.label}
              value={summaryCardContent}
              description={summaryCardContent?.description}
              childElements={null}
              isSelected={false}
              editElement={
                (
                  ((![
                    AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
                    AD_TYPES.SPONSORED_DISPLAY_V2.value,
                    AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
                    AD_TYPES.SPONSORED_BRAND_WALMART.value,
                    AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                    AD_TYPES.SPONSORED_PRODUCT_GENERIC.value,
                    AD_TYPES.SPONSORED_DISPLAY.value,
                    AD_TYPES.CUSTOM.value,
                  ].includes(state.adType) &&
                    !(state.adType === AD_TYPES.SPONSORED_PRODUCT.value && state.hasParent)) ||
                    (![
                      AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
                      AD_TYPES.SPONSORED_BRAND_WALMART.value,
                      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                    ].includes(state.adType) &&
                      state.channel === "walmart")) &&
                  !review &&
                  !state.id
                ) ?
                  <span
                    onClick={() => onChange({ targetType: null })}
                    style={{
                      textTransform: "uppercase",
                      fontSize: "12px",
                      color: "#333",
                    }}
                  >
                    {t(($) => $.advertising["Change Target Type"])} <i className="fa fa-pencil" />
                  </span>
                : null
              }
              isSummaryCard={isSummaryCard}
            />
          : targetingOptions.map((type) => {
              return (
                <AdPlanChoiceCard
                  key={type.value}
                  label={type.label}
                  value={type}
                  description={type.description}
                  childElements={null}
                  isSelected={type.value === state.targetType}
                  onChange={(value) => {
                    if (value.value !== TARGET_TYPES.FUNNEL.value) {
                      onChange({
                        targetType: value.value,
                        filledProductKeys: [],
                        filledProductBrands: [],
                        filteredAttributes: {
                          color: [],
                          size: [],
                          collection: [],
                          flavor: [],
                          material: [],
                          other: [],
                        },
                      });
                    } else {
                      onChange({ targetType: value.value });
                    }
                  }}
                />
              );
            })
          }
        </Col>
      </Row>
    </FieldWrapper>
  );
};

export const TagsField = ({ state, onChange, errors, fieldsToValidate, review, channel }) => {
  const { t } = useTranslation(["advertising"]);
  const marketPlace = useAppSelector((s) => s.marketPlace);
  if (
    state.adType !== AD_TYPES.SPONSORED_PRODUCT.value &&
    state.adType !== AD_TYPES.SPONSORED_BRAND.value &&
    state.adType !== AD_TYPES.SPONSORED_BRAND_VIDEO.value &&
    state.adType !== AD_TYPES.SPONSORED_PRODUCT_AUTO.value &&
    state.adType !== AD_TYPES.CUSTOM.value &&
    channel !== "walmart"
  )
    return null;

  if (
    state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value ||
    state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
  )
    return null;

  const productIds = state.product || [];
  const filledTags = state.filledTags || [];

  return (
    <FieldWrapper>
      <div style={{ padding: review ? "0 2rem" : "2rem" }}>
        <div style={{ overflow: "auto" }}>
          {review ?
            <TagsReviewDisplay
              tags={filledTags}
              label={t(($) => $.advertising["Your Additional Keywords"])}
              toolTip={t(
                ($) =>
                  $.advertising[
                    "If you want your targeting plan to include specific keywords, add them here. You can add them anytime."
                  ],
              )}
            />
          : <KeywordListInput
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  <span>
                    {state.targetType === TARGET_TYPES.AUTOMATIC.value ?
                      t(($) => $.advertising["Your Additional Keywords (Optional)"])
                    : t(($) => $.advertising["Your Additional Keywords"])}
                  </span>
                  <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                    {channel === "walmart" ?
                      t(
                        ($) =>
                          $.advertising[
                            "If you want your targeting plan to include specific keywords and item IDs, add them here. You can add them anytime."
                          ],
                      )
                    : t(
                        ($) =>
                          $.advertising[
                            "If you want your targeting plan to include specific keywords, add them here. You can add them anytime."
                          ],
                      )
                    }
                  </HoverInfo>
                </span>
              }
              value={filledTags}
              onChange={(filled) => onChange({ filledTags: filled })}
              placeholder={t(($) => $.advertising["Enter keywords"])}
              bulkEditTitle={t(($) => $.advertising["Additional Keywords"])}
              lockedValues={state.savedAdPlan?.filledTags}
              lockedValueHint={t(
                ($) => $.advertising["Already saved to this media plan, so it can't be removed here."],
              )}
            />
          }
        </div>

        {errors.filledTags && <small className="text-danger">{errors.filledTags}</small>}
        {channel === "walmart" && errors.filledTagsOrAsins && fieldsToValidate?.includes("filledTagsOrAsins") && (
          <small className="text-danger">{errors.filledTagsOrAsins}</small>
        )}

        {!review &&
          ["manual"].includes(state.targetType) &&
          state.adType === AD_TYPES.SPONSORED_PRODUCT.value &&
          ["AMZ"].includes(state.channel) && (
            <KeywordSelector
              productIds={productIds.map((id) => String(id))}
              marketplace={marketPlace.marketPlace}
              filledTags={filledTags}
              onChangeFilledTags={(next) => onChange({ filledTags: next })}
            />
          )}
      </div>
    </FieldWrapper>
  );
};

export const AsinsField = ({ state, onChange, errors, fieldsToValidate, review, channel }) => {
  const { t } = useTranslation(["advertising"]);
  if (
    ![
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      AD_TYPES.SPONSORED_DISPLAY_V2.value,
      AD_TYPES.CUSTOM.value,
    ].includes(state.adType) ||
    channel === "walmart"
  ) {
    return null;
  }

  if (
    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
    state.sdCampaignType !== SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL.value
  ) {
    return null;
  }

  if (state.adType === AD_TYPES.SPONSORED_BRAND.value && state.autoCollection) {
    return null;
  }

  if ([channel, state.channel]?.includes("walmart")) {
    return null;
  }

  return (
    <FieldWrapper>
      <div
        style={{
          padding: review && state.filledAsins?.length < 1 ? 0 : "2rem",
        }}
      >
        <div style={{ overflow: "auto" }}>
          {review ?
            <TagsReviewDisplay
              tags={state.filledAsins}
              label={t(($) => $.advertising["Your Additional ASINs"])}
              toolTip={t(($) => $.advertising["Add themed ASINs to your media plan"])}
            />
          : <AsinListInput
              label={
                <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                  <span>
                    {(
                      state.targetType === TARGET_TYPES.AUTOMATIC.value ||
                      state.targetType === TARGET_TYPES.FUNNEL.value
                    ) ?
                      t(($) => $.advertising["Your Additional ASINs (Optional)"])
                    : t(($) => $.advertising["Your Additional ASINs"])}
                  </span>
                  <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                    {t(($) => $.advertising["Add themed ASINs to your media plan"])}
                  </HoverInfo>
                </span>
              }
              value={state.filledAsins ?? []}
              onChange={(filledAsins) => onChange({ filledAsins })}
              placeholder={t(($) => $.advertising["Enter ASINs..."])}
              bulkEditTitle={t(($) => $.advertising["Additional ASINs"])}
              lockedValues={state.savedAdPlan?.filledAsins}
              lockedValueHint={t(
                ($) => $.advertising["Already saved to this media plan, so it can't be removed here."],
              )}
            />
          }
        </div>
        {errors.filledAsins && fieldsToValidate?.includes("filledAsins") && (
          <small className="text-danger">{errors.filledAsins}</small>
        )}
        {errors.filledTagsOrAsins && fieldsToValidate?.includes("filledTagsOrAsins") && (
          <small className="text-danger">{errors.filledTagsOrAsins}</small>
        )}
      </div>
    </FieldWrapper>
  );
};

export const NegativeKeywordsField = ({ state, onChange, errors, channel, isDisabled }) => {
  const { t } = useTranslation(["advertising"]);
  const supportedAdTypes =
    channel === "walmart" ?
      WALMART_NEGATIVE_KEYWORD_AD_TYPES
    : [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ];
  if (!supportedAdTypes.includes(state.adType)) {
    return null;
  }

  return (
    <FieldWrapper>
      <div>
        {isDisabled ?
          <TagsReviewDisplay
            tags={state.filledNegativeKeywords}
            label={t(($) => $.advertising["Your Negative Keywords"])}
            toolTip={t(
              ($) =>
                $.advertising[
                  "If you want your targeting plan to negate specific keywords, add them here. You can add them anytime."
                ],
            )}
          />
        : <KeywordListInput
            label={
              <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                <span>
                  {state.targetType === TARGET_TYPES.AUTOMATIC.value ?
                    t(($) => $.advertising["Your Negative Keywords (Optional)"])
                  : t(($) => $.advertising["Your Negative Keywords"])}
                </span>
                <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                  {t(
                    ($) =>
                      $.advertising[
                        "If you want your targeting plan to negate specific keywords, add them here. You can add them anytime."
                      ],
                  )}
                </HoverInfo>
              </span>
            }
            value={state.filledNegativeKeywords ?? []}
            onChange={(filledNegativeKeywords) => onChange({ filledNegativeKeywords })}
            placeholder={t(($) => $.advertising["Keywords"])}
            bulkEditTitle={t(($) => $.advertising["Negative Keywords"])}
            lockedValues={state.savedAdPlan?.filledNegativeKeywords}
            lockedValueHint={t(($) => $.advertising["Already saved to this media plan, so it can't be removed here."])}
          />
        }
        {errors.filledNegativeKeywords && <small className="text-danger">{errors.filledNegativeKeywords}</small>}
      </div>
    </FieldWrapper>
  );
};

export const NegativeAsinsField = ({ state, onChange, errors, channel, isDisabled }) => {
  const { t } = useTranslation(["advertising"]);
  if (
    (state.adType !== AD_TYPES.SPONSORED_PRODUCT.value && state.adType !== AD_TYPES.SPONSORED_PRODUCT_AUTO.value) ||
    channel !== "amazon"
  ) {
    return null;
  }

  return (
    <FieldWrapper>
      <div className="mt-5">
        {isDisabled ?
          <TagsReviewDisplay
            tags={state.filledNegativeAsins}
            label={t(($) => $.advertising["Your Negative ASINs"])}
            toolTip={t(
              ($) =>
                $.advertising[
                  "If you want your targeting plan to negate specific ASINs, add them here. You can add them anytime."
                ],
            )}
          />
        : <AsinListInput
            label={
              <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                <span>
                  {state.targetType === TARGET_TYPES.AUTOMATIC.value ?
                    t(($) => $.advertising["Your Negative ASINs (Optional)"])
                  : t(($) => $.advertising["Your Negative ASINs"])}
                </span>
                <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                  {t(
                    ($) =>
                      $.advertising[
                        "If you want your targeting plan to negate specific ASINs, add them here. You can add them anytime."
                      ],
                  )}
                </HoverInfo>
              </span>
            }
            value={state.filledNegativeAsins ?? []}
            onChange={(filledNegativeAsins) => onChange({ filledNegativeAsins })}
            placeholder={t(($) => $.advertising["Negative ASINs"])}
            bulkEditTitle={t(($) => $.advertising["Negative ASINs"])}
            lockedValues={state.savedAdPlan?.filledNegativeAsins}
            lockedValueHint={t(($) => $.advertising["Already saved to this media plan, so it can't be removed here."])}
          />
        }
        {errors.filledNegativeAsins && <small className="text-danger">{errors.filledNegativeAsins}</small>}
      </div>
    </FieldWrapper>
  );
};

export const ToggleStatusField = ({ state, onChange }) => {
  const { t } = useTranslation(["advertising"]);
  const queryClient = useQueryClient();

  const updateStatusHandler = useMutation({
    mutationFn: async (body) => {
      const { state, id } = body.data ?? {};

      const reqData = { id, state };
      const skip = body.skip ?? false;
      if (skip) {
        return true;
      }
      return await api.post("/api/set_campaign_state/", reqData);
    },

    onSuccess: (data, variables) => {
      successToast({
        message: t(($) => $.advertising["Updating Media Plan"]),
      });

      // The mutation succeeded, so the server really is in this state now; record it on the
      // snapshot so the enable/pause button flips without waiting for a refetch.
      onChange({
        advStatus: variables.data.state,
        savedAdPlan: { ...state.savedAdPlan, advStatus: variables.data.state },
      });
      queryClient.invalidateQueries({
        queryKey: ["ad_report"],
      });
      return data;
    },

    onError: (error, variables) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Update Media Plan {name}"], { name: variables.data.name }),
        error,
      });
    },
  });

  return (
    <>
      {state.savedAdPlan?.advStatus === AD_STATES.PAUSE.value && (
        <Col xs={12} lg={4} className="px-3 d-flex align-items-end justify-content-end pb-3">
          <Button
            isDisabled={updateStatusHandler.isPending}
            onPress={() => {
              updateStatusHandler.mutate({
                data: {
                  id: state.id,
                  state: AD_STATES.ACTIVE.value,
                },
              });
            }}
          >
            {t(($) => $.advertising["Enable Media Plan"])}
          </Button>
        </Col>
      )}
      {state.savedAdPlan?.advStatus === AD_STATES.ACTIVE.value && (
        <Col xs={12} lg={4} className="px-3 d-flex align-items-end justify-content-end pb-2">
          <Button
            isDisabled={updateStatusHandler.isPending}
            onPress={() => {
              updateStatusHandler.mutate({
                data: {
                  id: state.id,
                  state: AD_STATES.PAUSE.value,
                },
              });
            }}
          >
            {t(($) => $.advertising["Pause Media Plan"])}
          </Button>
        </Col>
      )}
    </>
  );
};

export const AdStatusField = ({ state, onChange, user, channel, isDisabled }) => {
  const { t } = useTranslation(["advertising"]);
  if (state.canHaveChildren) {
    return null;
  }
  const rawStateOptions =
    channel === "walmart" ?
      [AD_STATES.PAUSE, AD_STATES.ACTIVE]
    : [
        AD_STATES.PAUSE,
        AD_STATES.ACTIVE,
        {
          ...AD_STATES.ARCHIVE,
          isDisabled: state.advStatus === AD_STATES.ACTIVE.value,
        },
      ];
  const stateOptions = rawStateOptions.map((o) => ({
    ...o,
    label: typeof o.label === "function" ? t(o.label) : o.label,
  }));

  return (
    <FieldWrapper displayError={state.advStatus === AD_STATES.ARCHIVE.value && user.organization_admin}>
      <Row>
        <Col xs={12} lg={4} className="px-3">
          <FormGroupTile
            key={state.advStatus}
            type="select"
            stateName={"advStatus"}
            toolTip={t(($) => $.advertising["Enable or Pause advertising of the current product."])}
            handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
            label={t(($) => $.advertising["Advertising State"])}
            defaultValue={stateOptions.find((o) => o.value === state.advStatus)}
            options={stateOptions}
            disabled={isDisabled}
          />
        </Col>
      </Row>
    </FieldWrapper>
  );
};

export const AdTypeField = ({
  state,
  onChange,
  adId,
  marketPlace,
  isChild,
  user,
  channel,
  errors,
  fieldsToValidate,
  brandOptions,
  isSummaryCard,
  setEditStep,
  review,
  categories,
  dashboard,
  hidden,
}) => {
  const { t } = useTranslation(["advertising"]);
  const hasSMBStarterSubscriptionModule = useModuleEnabled("SMB_STARTER");

  if (state.canHaveChildren || hidden) {
    return null;
  }

  const WALMART_SP_DAILY_BUDGET_DEFAULT = user.organization_type === "vendor" ? 126 : 26;
  const WALMART_SINGLE_CAMPAIGN_DAILY_BUDGET_DEFAULT = user.organization_type === "vendor" ? 50 : 10;

  const adTypes = {
    amazon:
      isChild ? [AD_TYPES.SPONSORED_PRODUCT, AD_TYPES.SPONSORED_PRODUCT_AUTO, AD_TYPES.SPONSORED_PRODUCT_GENERIC]
      : user.organization_type === "vendor" || user.brand_registered !== "False" ?
        [
          AD_TYPES.SPONSORED_PRODUCT,
          AD_TYPES.SPONSORED_BRAND,
          AD_TYPES.SPONSORED_BRAND_VIDEO,
          AD_TYPES.SPONSORED_PRODUCT_AUTO,
          AD_TYPES.SPONSORED_DISPLAY_V2,
        ]
      : [
          AD_TYPES.SPONSORED_PRODUCT,
          AD_TYPES.SPONSORED_BRAND,
          AD_TYPES.SPONSORED_BRAND_VIDEO,
          AD_TYPES.SPONSORED_PRODUCT_AUTO,
        ],
    walmart: [
      {
        ...AD_TYPES.SPONSORED_PRODUCT,
        description: "Display individual product listings on Walmart product pages and related shopping results.",
      },
      AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART,
      AD_TYPES.SPONSORED_BRAND_WALMART,
      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART,
    ],
  };

  const adTypeOptions =
    [GOALS.SOV.value].includes(state.goal) ?
      {
        amazon: [AD_TYPES.SPONSORED_PRODUCT],
        walmart: [AD_TYPES.SPONSORED_PRODUCT],
      }
    : hasSMBStarterSubscriptionModule ?
      {
        amazon: [
          AD_TYPES.SPONSORED_PRODUCT,
          AD_TYPES.SPONSORED_PRODUCT_AUTO,
          AD_TYPES.SPONSORED_BRAND,
          AD_TYPES.SPONSORED_BRAND_VIDEO,
          AD_TYPES.SPONSORED_DISPLAY_V2,
        ],
        walmart: [],
      }
    : adTypes;

  const summaryCardContent = adTypeOptions[channel]?.find((choice) => choice.value === state.adType);

  return (
    <FieldWrapper>
      <Row style={{ maxWidth: "90%" }}>
        <Col xs={12}>
          {isSummaryCard && summaryCardContent ?
            <AdPlanChoiceCard
              label={summaryCardContent.label}
              value={summaryCardContent}
              description={summaryCardContent.description}
              childElements={null}
              editElement={
                <span
                  onClick={() => setEditStep(1)}
                  style={{
                    textTransform: "uppercase",
                    fontSize: "12px",
                    color: "#333",
                  }}
                >
                  {t(($) => $.advertising["Edit Ad Type"])} <i className="fa fa-pencil" />
                </span>
              }
              isSelected={false}
              isSummaryCard={isSummaryCard}
            />
          : adTypeOptions[channel]?.map((type) => {
              const isDisabledForSMBStarterSubscription =
                hasSMBStarterSubscriptionModule && !SMB_STARTER_ALLOWED_AD_TYPES.includes(type.value);

              return (
                <AdPlanChoiceCard
                  key={`${type.value}-${channel}`}
                  label={type.label}
                  value={type}
                  description={type.description}
                  disabled={type.value === AD_TYPES.SPONSORED_DISPLAY.value || isDisabledForSMBStarterSubscription}
                  overrideStyles={
                    (
                      (type.value === AD_TYPES.SPONSORED_BRAND.value && channel === "walmart") ||
                      type.value === AD_TYPES.SPONSORED_DISPLAY.value ||
                      isDisabledForSMBStarterSubscription
                    ) ?
                      {
                        backgroundColor: "#F8F8F8",
                        pointerEvents: "not-allowed",
                        color: "#999",
                        marginTop: token("spacing.small"),
                        marginBottom: token("spacing.small"),
                      }
                    : {}
                  }
                  childElements={
                    channel === "walmart" && type.value === state.adType ?
                      <AcosGoalsField
                        state={state}
                        onChange={onChange}
                        marketPlace={marketPlace}
                        errors={errors}
                        isChild={isChild}
                        roas={user.preferences.acos_or_roas === "roas"}
                      />
                    : type.value === state.adType && type.value === AD_TYPES.SPONSORED_BRAND.value ?
                      [
                        <LandingPageDetailsField
                          key="LandingPageDetailsField"
                          state={state}
                          onChange={onChange}
                          errors={errors}
                          fieldsToValidate={fieldsToValidate}
                          adId={adId}
                          brandOptions={brandOptions}
                          review={review}
                          categories={categories}
                          dashboard={dashboard}
                        />,
                        <LandingPageField
                          key="LandingPageField"
                          state={state}
                          onChange={onChange}
                          errors={errors}
                          fieldsToValidate={fieldsToValidate}
                          adId={adId}
                        />,
                      ]
                    : type.value === state.adType && type.value === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                      [
                        <LandingPageDetailsField
                          key="LandingPageDetailsField2"
                          state={state}
                          onChange={onChange}
                          errors={errors}
                          fieldsToValidate={fieldsToValidate}
                          adId={adId}
                          brandOptions={brandOptions}
                          review={review}
                          categories={categories}
                          dashboard={dashboard}
                        />,
                        <VideoAdTypeField
                          key="VideoAdTypeField"
                          state={state}
                          onChange={onChange}
                          errors={errors}
                          fieldsToValidate={fieldsToValidate}
                          review={review}
                          categories={categories}
                          dashboard={dashboard}
                        />,
                      ]
                    : null
                  }
                  isSelected={type.value === state.adType}
                  onChange={(value) => {
                    let prevAssetChanges = {};
                    if (
                      value.value !== state.adType &&
                      [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) &&
                      state.creativeAsset
                    ) {
                      prevAssetChanges = {
                        creativeAsset: null,
                        lifestyleImageDetails: null,
                        lifestyleImage: null,
                        logo: null,
                        logoDetails: null,
                        video: null,
                        videoDetails: null,
                      };
                    }
                    if (
                      (value.value === AD_TYPES.SPONSORED_PRODUCT_AUTO.value && state.hasParent) ||
                      (value.value === AD_TYPES.SPONSORED_DISPLAY.value && !state.hasParent)
                    ) {
                      onChange({
                        adType: value.value,
                        targetType: TARGET_TYPES.AUTOMATIC.value,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      if (value.value === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
                        onChange({
                          asinHarvestingEnabled: true,
                          campaignNames: getCampaignsByAdType(
                            value.value,
                            state.campaignNames,
                            state.autoCampaignEnabled,
                            state.name,
                          ),
                          ...prevAssetChanges,
                        });
                      }
                      return;
                    }
                    if (value.value === AD_TYPES.SPONSORED_PRODUCT.value && state.hasParent) {
                      onChange({
                        adType: value.value,
                        targetType: TARGET_TYPES.MANUAL.value,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      return;
                    }

                    if (channel === "walmart") {
                      if (
                        [AD_TYPES.SPONSORED_PRODUCT_WALMART.value, AD_TYPES.SPONSORED_PRODUCT.value].includes(
                          value.value,
                        ) &&
                        state.goal !== GOALS.SOV.value
                      ) {
                        onChange({
                          dailyBudget: WALMART_SP_DAILY_BUDGET_DEFAULT,
                          targetType: TARGET_TYPES.MANUAL.value,
                          adType: value.value,
                          minBid: null,
                        });
                        return;
                      }
                      if (
                        [
                          AD_TYPES.SPONSORED_BRAND_WALMART.value,
                          AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                          AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
                        ].includes(value.value)
                      ) {
                        onChange({
                          adType: value.value,
                          minBid:
                            value.value === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value ? null
                            : value.value === AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value ? WALMART_SBV_MIN_BID_DEFAULT
                            : WALMART_SB_MIN_BID_DEFAULT,
                          dailyBudget: WALMART_SINGLE_CAMPAIGN_DAILY_BUDGET_DEFAULT,
                          targetType:
                            value.value === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value ?
                              TARGET_TYPES.AUTOMATIC.value
                            : TARGET_TYPES.MANUAL.value,
                        });
                        return;
                      }
                    }
                    if (value.value === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
                      onChange({
                        adType: value.value,
                        targetType: AD_TYPES.SPONSORED_DISPLAY_V2.targetType,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      return;
                    }
                    if (
                      [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(value.value) &&
                      state.targetType !== TARGET_TYPES.FUNNEL.value
                    ) {
                      onChange({
                        adType: value.value,
                        targetType: TARGET_TYPES.FUNNEL.value,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      return;
                    }

                    if (
                      (value.value === AD_TYPES.SPONSORED_BRAND.value ||
                        value.value === AD_TYPES.SPONSORED_BRAND_VIDEO.value) &&
                      !state.id
                    ) {
                      onChange({
                        bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                        adType: value.value,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      return;
                    }

                    if (value.value !== AD_TYPES.SPONSORED_BRAND.value) {
                      onChange({
                        storeSpotlight: false,
                        storeSpotlightSubpages: [],
                        newProductCollection: false,
                        manualCollection: false,
                        autoCollection: false,
                        excludedProducts: [],
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                    }

                    if (value.value !== AD_TYPES.SPONSORED_PRODUCT_AUTO.value && state.autoNegationEnabled) {
                      onChange({
                        maxAcosNegationThreshold: null,
                        cumulativeNegationThreshold: null,
                        autoNegationEnabled: false,
                        adType: value.value,
                        campaignNames: getCampaignsByAdType(
                          value.value,
                          state.campaignNames,
                          state.autoCampaignEnabled,
                          state.name,
                        ),
                        ...prevAssetChanges,
                      });
                      return;
                    }

                    onChange({
                      adType: value.value,
                      campaignNames: getCampaignsByAdType(
                        value.value,
                        state.campaignNames,
                        state.autoCampaignEnabled,
                        state.name,
                      ),
                      ...prevAssetChanges,
                    });
                  }}
                />
              );
            })
          }
          {user.organization_type === "seller" && user.brand_registered === "False" && channel === "amazon" && (
            <div
              style={{
                padding: "0 100px",
              }}
            >
              <div />
              <p
                style={{
                  color: "#73706E",
                }}
                className="fs-standard w-75"
              >
                {t(
                  ($) =>
                    $.advertising[
                      "To enable Sponsored Display, please ensure you have at least one brand registered with Amazon."
                    ],
                )}
              </p>
            </div>
          )}
          {errors.adType && fieldsToValidate?.includes("adType") && (
            <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
              {errors.adType}
            </small>
          )}
        </Col>
      </Row>
    </FieldWrapper>
  );
};

const ProductLabel = ({ product }) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "50px auto auto",
        alignContent: "center",
        borderColor: "#FF0000",
      }}
    >
      <div style={{ maxWidth: "50px", display: "flex", alignItems: "center" }}>
        <ProductImage id={product.value} width="50px" height="50px" />{" "}
      </div>
      <div style={{ padding: "10px" }}>
        <span style={{ marginLeft: "1.5rem", marginBottom: "-.25rem" }}>{product.sku}</span>
        <span
          style={{
            marginLeft: "1.5rem",
            marginTop: "-.05rem",
            fontSize: "1.1rem",
            display: "block",
            textTransform: "none",
            whiteSpace: "pre-line",
          }}
        >
          {product.product_title}
        </span>
      </div>
    </div>
  );
};

const BrandLogoLabel = ({ logo }) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "35px auto",
        alignContent: "center",
        borderColor: "#FF0000",
      }}
    >
      <div style={{ maxWidth: "35px", display: "flex", alignItems: "center" }}>
        <img src={logo.url} alt={""} style={{ height: "30px", width: "30px" }} />
      </div>
      <div style={{ padding: "10px" }}>
        <span style={{ marginLeft: "1.5rem", marginBottom: "-.25rem" }}>{logo.label}</span>
      </div>
    </div>
  );
};

export const ProductsField = ({ state, onChange, adId, errors, fieldsToValidate, review, isModal }) => {
  if (
    ![
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
      AD_TYPES.SPONSORED_PRODUCT_GENERIC.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      AD_TYPES.SPONSORED_DISPLAY.value,
      AD_TYPES.SPONSORED_DISPLAY_V2.value,
    ].includes(state.adType) &&
    !state.canHaveChildren &&
    state.channel !== "walmart"
  ) {
    return null;
  }
  if (
    state.adType === AD_TYPES.SPONSORED_BRAND.value ||
    (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.storeLandingPage)
  ) {
    return null;
  }

  const onProductsChange = (key, value) => {
    onChange({
      [key]: value.filter((p) => !p.isDisabled).map((p) => p.value),
    });
  };

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <FieldWrapper>
        <Row style={{ marginTop: review ? 0 : "2rem" }}>
          <Col md={12}>
            <ProductSelectorField
              state={state}
              stateName={"product"}
              handleChildFormElement={onProductsChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              review={review}
              isModal={isModal}
            />
          </Col>
        </Row>
      </FieldWrapper>
    </div>
  );
};

export const ExcludedProductsField = ({ state, onChange, adId, errors, fieldsToValidate, review, isModal }) => {
  if (state.adType !== AD_TYPES.SPONSORED_BRAND.value || !state.autoCollection) {
    return null;
  }

  const reviewState = review || adId !== undefined || state.id !== undefined;
  if (reviewState && !state.excludedProducts?.length) {
    return null;
  }

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <FieldWrapper>
        <Row style={{ marginTop: review ? 0 : "2rem" }}>
          <Col md={12}>
            <ProductSelectorField
              state={state}
              stateName={"excludedProducts"}
              handleChildFormElement={(key, value) => onChange({ [key]: value.map((p) => p.value) })}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              review={reviewState}
              isModal={isModal}
              isExclusionProductSelector={true}
            />
          </Col>
        </Row>
      </FieldWrapper>
    </div>
  );
};

const LandingPageField = ({ state, onChange, adId, errors, fieldsToValidate }) => {
  const { t } = useTranslation(["advertising"]);
  if (state.adType !== AD_TYPES.SPONSORED_BRAND.value) {
    return null;
  }

  const formatIsLocked = adId !== undefined || state.id !== undefined;
  const formatResetFields = {
    headline: "",
    selectedProducts: [],
    selectedAsins: [],
    selectedStorePageProducts: [],
    selectedStorePageAsins: [],
    selectedLandingPageProducts: [],
    selectedLandingPageAsins: [],
    selectedCreativeProducts: [],
    selectedCreativeAsins: [],
    store: "",
    page: "",
    logo: "",
    landingPageProduct: [],
    storePageProduct: [],
    creativeProduct: [],
  };

  return (
    <Row style={{ marginTop: "1rem", marginBottom: "1rem" }}>
      <Col xs={12}>
        <div style={{ marginBottom: "2rem" }}>
          <FormLabel>
            <h6
              className="title"
              style={{
                color: "#73706E",
                fontWeight: "700",
                textTransform: "none",
              }}
            >
              {t(($) => $.advertising["Campaign Landing Page"])}
              <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                {t(
                  ($) =>
                    $.advertising[
                      "This landing page will elevate your sponsored brand advertisement by showcasing additional products and information."
                    ],
                )}
              </HoverInfo>
            </h6>
          </FormLabel>
        </div>
        <Row style={{ display: "flex" }}>
          {state.newProductCollection && (
            <Col xs={6} className="pe-3">
              <Checkbox
                label={t(($) => $.advertising["Product Collection"])}
                value={state.newProductCollection}
                isDisabled={formatIsLocked}
                onChange={() => {
                  onChange({
                    newProductCollection: !state.newProductCollection,
                    storeLandingPage: !state.newProductCollection,
                    bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                    manualCollection: false,
                    autoCollection: false,
                    newLandingPage: false,
                    storeSpotlight: false,
                    ...formatResetFields,
                  });
                }}
              />
            </Col>
          )}
          <Col xs={6} className="pe-3">
            <Checkbox
              label={t(($) => $.advertising["Manual Collection"])}
              value={state.manualCollection}
              isDisabled={formatIsLocked}
              onChange={() => {
                onChange({
                  manualCollection: !state.manualCollection,
                  storeLandingPage: !state.manualCollection,
                  bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                  lifestyleImage: null,
                  lifestyleImageDetails: null,
                  autoCollection: false,
                  newProductCollection: false,
                  newLandingPage: false,
                  storeSpotlight: false,
                  ...formatResetFields,
                });
              }}
            />
          </Col>
          <Col xs={6} className="pe-3">
            <Checkbox
              label={t(($) => $.advertising["Auto Collection"])}
              value={state.autoCollection}
              isDisabled={formatIsLocked}
              onChange={() => {
                onChange({
                  autoCollection: !state.autoCollection,
                  bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                  lifestyleImage: null,
                  lifestyleImageDetails: null,
                  manualCollection: false,
                  newProductCollection: false,
                  newLandingPage: false,
                  storeLandingPage: false,
                  storeSpotlight: false,
                  ...formatResetFields,
                });
              }}
            />
          </Col>
          <Col xs={6} className="pe-3">
            <Checkbox
              label={t(($) => $.advertising["Store Spotlight"])}
              value={state.storeSpotlight}
              isDisabled={formatIsLocked}
              onChange={() => {
                onChange({
                  storeSpotlight: !state.storeSpotlight,
                  bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                  lifestyleImage: null,
                  lifestyleImageDetails: null,
                  newProductCollection: false,
                  manualCollection: false,
                  autoCollection: false,
                  newLandingPage: false,
                  storeLandingPage: false,
                  ...formatResetFields,
                });
              }}
            />
          </Col>
        </Row>
        {errors.landingPage && fieldsToValidate?.includes("landingPage") && (
          <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
            {errors.landingPage}
          </small>
        )}
      </Col>
    </Row>
  );
};

const VideoAdTypeField = ({ state, onChange, errors, fieldsToValidate, categories, dashboard, review }) => {
  const { t } = useTranslation(["advertising"]);
  if (state.adType !== AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
    return null;
  }

  return (
    <>
      <Row style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <Col xs={12}>
          <div style={{ marginBottom: "2rem" }}>
            <FormLabel>
              <h6
                className="title"
                style={{
                  color: "#73706E",
                  fontWeight: "700",
                  textTransform: "none",
                }}
              >
                {t(($) => $.advertising["Video Ad Type"])}
                <HoverInfo iconCss={{ color: "grey", display: "inline", paddingStart: "small" }}>
                  {t(($) => $.advertising["The type of video advertisement your creative asset will support"])}
                </HoverInfo>
              </h6>
            </FormLabel>
          </div>
          <Row>
            {[VIDEO_AD_TYPES.VIDEO, VIDEO_AD_TYPES.BRAND_VIDEO].map((videoAdType) => {
              return (
                <Col key={videoAdType.value} xs={12} lg={4}>
                  <Checkbox
                    label={typeof videoAdType.label === "function" ? t(videoAdType.label) : videoAdType.label}
                    data-value={videoAdType.value}
                    value={state.videoAdType === videoAdType.value}
                    onChange={() => {
                      onChange({
                        videoAdType: videoAdType.value,
                        bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                        storeLandingPage: videoAdType.value === VIDEO_AD_TYPES.BRAND_VIDEO.value,
                      });
                    }}
                  />
                </Col>
              );
            })}
          </Row>
          {errors.landingPage && fieldsToValidate?.includes("landingPage") && (
            <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
              {errors.videoAdType}
            </small>
          )}
        </Col>
      </Row>
      {state.storeLandingPage && (
        <Row>
          <Col xs={12} lg={6} className="pb-3">
            <FormGroupTile
              type="select"
              multi={false}
              stateName={"categoryId"}
              handleChildFormElement={(key, value) => onChange({ categoryId: value.value })}
              label={t(($) => $.advertising["Select a Category"])}
              defaultValue={categories.find((c) => c.value === state.categoryId)}
              options={categories}
              disabled={!dashboard || review}
            />
            {errors.categoryId && fieldsToValidate?.includes("categoryId") && (
              <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
                {errors.categoryId}
              </small>
            )}
          </Col>
        </Row>
      )}
    </>
  );
};

export const LandingPageDetailsField = ({
  state,
  onChange,
  adId,
  review,
  categories,
  brandOptions,
  errors,
  fieldsToValidate,
  dashboard,
  isModal,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  const reviewState = adId || state.id || review;
  const reviewModeBrandValue = state.creativeBrandName ?? state.brand?.label;

  if (
    state.adType !== AD_TYPES.SPONSORED_BRAND.value &&
    !(state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.headline && reviewState) &&
    !state.id
  ) {
    return null;
  }

  return (
    <Row style={{ marginTop: "2rem", marginBottom: "0" }}>
      <Col xs={6} className="pe-5">
        <FormGroupTile
          type={reviewState ? "formcontrol" : "select"}
          stateName={"brand"}
          handleChildFormElement={(key, value) => {
            if (
              [AD_TYPES.SPONSORED_BRAND.value].includes(state.adType) &&
              ![
                SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                SB_BID_STRATEGY_OPTIONS.MAXIMIZE_NEW_TO_BRAND_CUSTOMERS.value,
              ].includes(state.bidStrategy) &&
              !adId
            ) {
              onChange({
                [key]: value,
                creativeBrandName: value?.label,
                bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
              });
              return;
            }
            onChange({ [key]: value, creativeBrandName: value?.label });
          }}
          label={reviewState ? t(($) => $.advertising["Brand"]) : t(($) => $.advertising["Select a Brand"])}
          labelClassName={"fs-4"}
          disabled={reviewState || isDisabled}
          defaultValue={reviewState ? reviewModeBrandValue : state.brand}
          options={brandOptions}
          selectClasses={reviewState ? "lightly-disabled" : null}
        />
        {errors.brand && fieldsToValidate?.includes("brand") && (
          <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
            {errors.brand}
          </small>
        )}
      </Col>
      {!reviewState ?
        <Col xs={6} className="ps-5">
          <FormGroupTile
            type="select"
            multi={false}
            stateName={"categoryId"}
            handleChildFormElement={(key, value) => onChange({ categoryId: value.value })}
            label={t(($) => $.advertising["Select a Category"])}
            defaultValue={categories.find((c) => parseInt(c.value) === parseInt(state.categoryId))}
            options={categories}
            disabled={!dashboard || review || isDisabled}
          />
          {errors.categoryId && fieldsToValidate?.includes("categoryId") && (
            <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
              {errors.categoryId}
            </small>
          )}
        </Col>
      : <>
          {/* Collection headlines are optional (auto collection has none at all) — hide the
              empty field in review */}
          {((state.adType === AD_TYPES.SPONSORED_BRAND.value &&
            !((state.manualCollection || state.autoCollection) && !state.headline)) ||
            (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.headline)) && (
            <Col xs={6} className="ps-5">
              <FormGroupTile
                type="formcontrol"
                label={t(($) => $.advertising["Ad Headline"])}
                labelClassName={"fs-4"}
                formControlType={"string"}
                stateName={"headline"}
                handleChildFormElement={(key, value) => onChange({ [key]: value })}
                disabled={
                  (reviewState && state.adType) ||
                  (state.id &&
                    isModal &&
                    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                    state.advModerationStatus === MODERATION_STATUSES.PENDING.value) ||
                  isDisabled
                }
                placeholder={t(($) => $.advertising["Headline"])}
                defaultValue={state.headline}
              />
            </Col>
          )}
        </>
      }
    </Row>
  );
};

export const StoreLandingPageFields = ({
  state,
  onChange,
  review,
  adId,
  storeOptions,
  pageOptions,
  brandOptions,
  errors,
  fieldsToValidate,
  reviewChildren,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  if (
    ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) ||
    (!state.storeLandingPage && !state.storeSpotlight)
  ) {
    return null;
  }

  const reviewState = adId || state.id || review;

  if (reviewState && state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.landingPageProduct?.length) {
    return null;
  }
  return (
    <Row
      style={{ marginBottom: "1rem" }}
      className={
        reviewState && state.headline ?
          (
            [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) &&
            reviewState &&
            !state.store?.label
          ) ?
            "my-0"
          : "my-5"
        : reviewState ?
          "mt-5"
        : "pe-3"
      }
    >
      {state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !(reviewState && state.headline) && (
        <Col md={6} className={reviewState ? "pe-5 mb-5" : "pe-5"}>
          <FormGroupTile
            type="select"
            stateName={"brand"}
            handleChildFormElement={(key, value) => onChange({ [key]: value, creativeBrandName: value?.label })}
            label={reviewState ? t(($) => $.advertising["Brand"]) : t(($) => $.advertising["Choose a Brand"])}
            disabled={reviewState || isDisabled}
            defaultValue={state.brand}
            options={brandOptions}
            selectClasses={reviewState ? "lightly-disabled" : null}
            components={
              reviewState ?
                {
                  Menu: () => null,
                  MenuList: () => null,
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }
              : {}
            }
          />
          {errors.brand && fieldsToValidate?.includes("brand") && (
            <div className="mt-3 ps-1">
              <small className="text-danger">{errors.brand}</small>
            </div>
          )}
        </Col>
      )}
      <Col
        md={6}
        className={
          state.storeSpotlight ? `pe-5 ${reviewState ? "mt-3" : "mt-3"}`
          : (
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value &&
            state.storeLandingPage &&
            !(reviewState && state.headline)
          ) ?
            "ps-5"
          : "pe-5"
        }
        hidden={
          [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) &&
          reviewState &&
          !state.store?.label
        }
      >
        <FormGroupTile
          type={reviewState ? "formcontrol" : "select"}
          stateName={"store"}
          handleChildFormElement={(key, value) => onChange({ [key]: value })}
          isClearable={
            state.newProductCollection ||
            state.manualCollection ||
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value
          }
          label={
            reviewState ? t(($) => $.advertising["Store"])
            : state.newProductCollection || state.manualCollection ?
              t(($) => $.advertising["Choose a Store (optional)"])
            : t(($) => $.advertising["Choose a Store"])
          }
          labelClassName={"fs-4"}
          disabled={reviewState || isDisabled}
          defaultValue={reviewState ? state.store?.label : state.store}
          options={storeOptions}
        />
        {errors.store && fieldsToValidate?.includes("store") && (
          <div className="mt-3 ps-1">
            <small className="text-danger">{errors.store}</small>
          </div>
        )}
      </Col>

      <Col
        md={6}
        className={
          state.storeSpotlight ? `ps-5 ${reviewState ? "mt-3" : "mt-3"}`
          : (
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value &&
            state.storeLandingPage &&
            !(reviewState && state.headline)
          ) ?
            reviewState ?
              "pe-5"
            : "pe-5 mt-5"
          : "ps-5"
        }
        hidden={
          ([AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) &&
            reviewState &&
            !state.page?.label) ||
          state.storeSpotlight
        }
      >
        <FormGroupTile
          type={reviewState ? "formcontrol" : "select"}
          stateName={"page"}
          handleChildFormElement={(key, value) => onChange({ [key]: value })}
          isClearable={
            state.newProductCollection ||
            state.manualCollection ||
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value
          }
          label={
            reviewState ? t(($) => $.advertising["Store Page"])
            : state.storeSpotlight ?
              t(($) => $.advertising["Choose a Landing Page"])
            : state.newProductCollection || state.manualCollection ?
              t(($) => $.advertising["Choose a Page (optional)"])
            : t(($) => $.advertising["Choose a Page"])
          }
          labelClassName={"fs-4"}
          disabled={reviewState || isDisabled}
          defaultValue={reviewState ? state.page?.label : state.page}
          options={pageOptions}
        />
        {errors.page && fieldsToValidate?.includes("page") && (
          <div className="mt-3 ps-1">
            <small className="text-danger">{errors.page}</small>
          </div>
        )}
      </Col>
      {reviewChildren}
    </Row>
  );
};

export const VideoField = ({
  state,
  onChange,
  errors,
  review,
  fieldsToValidate,
  savedVideos,
  brandOptions,
  adId,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  if (state.adType !== AD_TYPES.SPONSORED_BRAND_VIDEO.value || state.storeLandingPage) {
    return null;
  }
  const reviewState = adId || state.id || review;

  const videoCreativeOptions =
    state.videoDetails ?
      [state.videoDetails]?.map((asset) => {
        return {
          value: asset.id,
          url: asset.url,
          label: <AssetLabel asset={asset} />,
        };
      })
    : [];

  return (
    <div>
      {!adId && (
        <Row>
          <Col md={6} className={reviewState ? "pe-5 mb-5" : "pe-5"}>
            <FormGroupTile
              type="select"
              stateName={"brand"}
              handleChildFormElement={(key, value) => onChange({ [key]: value, creativeBrandName: value?.label })}
              label={reviewState ? t(($) => $.advertising["Brand"]) : t(($) => $.advertising["Choose a Brand"])}
              disabled={reviewState || isDisabled}
              defaultValue={state.brand}
              options={brandOptions}
              selectClasses={reviewState ? "lightly-disabled" : null}
              components={
                reviewState ?
                  {
                    Menu: () => null,
                    MenuList: () => null,
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  }
                : {}
              }
            />

            {errors.brand && fieldsToValidate?.includes("brand") && (
              <div className="mt-3 ps-1">
                <small className="text-danger">{errors.brand}</small>
              </div>
            )}
          </Col>
          <Col md={6} className={adId ? "ps-0 mb-5" : "ps-5"} hidden={reviewState}>
            <FormGroupTile
              type="select"
              stateName={"video"}
              handleChildFormElement={(key, value) => onChange({ [key]: value.value })}
              label={
                reviewState ?
                  t(($) => $.advertising["Video Creative"])
                : t(($) => $.advertising["Choose An Existing Video"])
              }
              disabled={true}
              defaultValue={videoCreativeOptions?.[0]}
              isClearable={!reviewState}
              options={savedVideos}
              selectClasses={"lightly-disabled"}
              components={{
                Menu: () => null,
                MenuList: () => null,
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
            />
            {errors.video && fieldsToValidate?.includes("video") && (
              <div className="mt-3 ps-1">
                <small className="text-danger">{errors.video}</small>
              </div>
            )}
          </Col>
          {state.brand && (
            <Col md={6} className="pe-5 pt-4">
              <FormGroupTile
                type="formcontrol"
                label={t(($) => $.advertising["Creative Brand Name"])}
                labelClassName={"fs-5"}
                formControlType={"string"}
                stateName={"creativeBrandName"}
                handleChildFormElement={(key, value) => onChange({ [key]: value })}
                disabled={reviewState || isDisabled}
                placeholder={t(($) => $.advertising["Brand Display Name"])}
                defaultValue={state.creativeBrandName}
              />
              <div style={{ textAlign: "left" }} className="pt-3">
                {errors.creativeBrandName && fieldsToValidate?.includes("creativeBrandName") && (
                  <small className="text-danger d-block" style={{ paddingLeft: ".8rem" }}>
                    {errors.creativeBrandName}
                  </small>
                )}
              </div>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export const CreativeField = ({
  state,
  onChange,
  errors,
  fieldsToValidate,
  adId,
  logoOptions,
  optional,
  creativesAssetSelection,
  review,
  isModal,
  isDisabled,
}) => {
  const { t } = useTranslation(["advertising"]);
  const showCreative =
    (!adId &&
      (state.adType === AD_TYPES.SPONSORED_BRAND.value ||
        (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.storeLandingPage))) ||
    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value;

  if (!showCreative) {
    return null;
  }

  const reviewState = review || state.id || adId;

  const brandLogoOptions = logoOptions?.map((l) => {
    return {
      ...l,
      label: <BrandLogoLabel logo={l} />,
    };
  });

  if (adId && creativesAssetSelection && !state.headline && !state.logo) {
    return null;
  }
  return (
    <>
      <div style={{ textAlign: "left" }}>
        {errors.creativeAssets && fieldsToValidate?.includes("creativeAssets") && (
          <small className="text-danger">{errors.creativeAssets}</small>
        )}
      </div>
      <div>
        <Row
          style={{ marginTop: "2rem" }}
          hidden={reviewState && !state.headline && !state.manualCollection && !state.autoCollection}
        >
          {!state.autoCollection && (
            <Col md={6} className="pe-5">
              <FormGroupTile
                type="formcontrol"
                label={
                  optional || state.manualCollection ?
                    t(($) => $.advertising["Ad Headline (optional)"])
                  : t(($) => $.advertising["Ad Headline"])
                }
                toolTip={
                  state.manualCollection ?
                    t(($) => $.advertising["If no headline is provided, Amazon will auto-generate one."])
                  : undefined
                }
                labelClassName={"fs-4"}
                formControlType={"string"}
                stateName={"headline"}
                handleChildFormElement={(key, value) => onChange({ [key]: value })}
                disabled={
                  (reviewState && state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value) ||
                  (state.id &&
                    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                    state.advModerationStatus === MODERATION_STATUSES.PENDING.value) ||
                  isDisabled
                }
                placeholder={t(($) => $.advertising["Headline"])}
                defaultValue={state.headline}
              />
              {!state.id ?
                <div className="d-flex justify-content-end pt-3 text-muted" style={{ fontSize: "10px" }}>
                  <small style={{ whiteSpace: "nowrap" }}>
                    {state.manualCollection ?
                      t(($) => $.advertising["{count} / 32 characters"], { count: state.headline?.length ?? 0 })
                    : t(($) => $.advertising["{count} / 50 characters"], { count: state.headline?.length ?? 0 })}
                  </small>
                </div>
              : null}
              <div style={{ textAlign: "left" }} className="pt-3">
                {errors.headline && fieldsToValidate?.includes("headline") && (
                  <small className="text-danger d-block">{errors.headline}</small>
                )}
              </div>
            </Col>
          )}
          {/* Auto collection hides the headline column, so the logo takes the left column slot */}
          <Col md={6} className={state.autoCollection ? "pe-5" : "ps-5"}>
            {(!reviewState ||
              (state.id &&
                state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                state.advModerationStatus !== MODERATION_STATUSES.PENDING.value &&
                isModal)) && (
              <FormGroupTile
                type="select"
                stateName={"logo"}
                handleChildFormElement={(key, value) => {
                  if (creativesAssetSelection && !value) {
                    onChange({
                      [key + "Details"]: null,
                      [key]: null,
                      creativeAsset: null,
                    });
                    return;
                  }
                  onChange({ [key]: value?.value });
                }}
                disabled={
                  ((state.id !== undefined || adId) &&
                    state.advModerationStatus === MODERATION_STATUSES.PENDING.value) ||
                  isDisabled
                }
                label={
                  (
                    ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) ||
                    state.manualCollection ||
                    state.autoCollection
                  ) ?
                    t(($) => $.advertising["Brand Logo (optional)"])
                  : t(($) => $.advertising["Brand Logo"])
                }
                labelClassName={"fs-4"}
                selectClasses="lightly-disabled"
                defaultValue={brandLogoOptions?.[0]}
                options={brandLogoOptions}
                isClearable={creativesAssetSelection && !review}
                isSearchable={!creativesAssetSelection}
                // Below two props used to display read-only field without placeholder or dropdown caret buttin
                components={
                  creativesAssetSelection ?
                    {
                      Menu: () => null,
                      MenuList: () => null,
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null,
                    }
                  : {}
                }
                placeholder={creativesAssetSelection ? null : t(($) => $.advertising["Select..."])}
                onClick={() => {
                  onChange({ assetSelectionType: "logo" });
                }}
              />
            )}
            {errors.logo && fieldsToValidate?.includes("logo") && (
              <div className="pt-4">
                <small className="text-danger">{errors.logo}</small>
              </div>
            )}
          </Col>
          {state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value && (
            <Col md={6} className={state.autoCollection ? "ps-5" : "pe-5 pt-3"}>
              <FormGroupTile
                type="formcontrol"
                label={t(($) => $.advertising["Creative Brand Name"])}
                labelClassName={"fs-4"}
                formControlType={"string"}
                stateName={"creativeBrandName"}
                handleChildFormElement={(key, value) => onChange({ [key]: value })}
                disabled={reviewState || isDisabled}
                placeholder={t(($) => $.advertising["Brand Display Name"])}
                defaultValue={state.creativeBrandName}
              />
              <div style={{ textAlign: "left" }} className="pt-3">
                {errors.creativeBrandName && fieldsToValidate?.includes("creativeBrandName") && (
                  <small className="text-danger d-block" style={{ paddingLeft: ".8rem" }}>
                    {errors.creativeBrandName}
                  </small>
                )}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};

export const CreativeProductsField = ({ state, onChange, errors, fieldsToValidate, adId, review, isModal }) => {
  if (
    (![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) ||
      !state.storeLandingPage) &&
    !(state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.logo && !state.store?.label)
  ) {
    return null;
  }

  if (
    state.newProductCollection ||
    state.manualCollection ||
    state.autoCollection ||
    (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.storeLandingPage)
  ) {
    return null;
  }

  // A store page would scope the selector to its own product ids, but `page` only ever carries
  // `{ value, label }`, so in practice this selector is unscoped and picks from the catalog.
  const selectableProductIds = state.page?.products ?? null;

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <Row style={{ marginTop: review ? 0 : "2rem" }}>
        <Col md={12}>
          <ProductSelectorField
            state={state}
            stateName={"creativeProduct"}
            selectableProductIds={selectableProductIds}
            handleChildFormElement={(key, value) => onChange({ [key]: value.map((p) => p.value) })}
            errors={errors}
            fieldsToValidate={fieldsToValidate}
            review={review}
            isModal={isModal}
            isCreativeProductSelector={true}
          />
        </Col>
      </Row>
    </div>
  );
};

export const LandingPageProductsField = ({ state, onChange, adId, errors, fieldsToValidate, review, isModal }) => {
  // Manual Collection has no landing page of its own to stock. Its only new-landing-page shape is
  // {"pageType": "PRODUCT_LIST"} with no product list attached, and Amazon builds that page from
  // the creative's asins, so a separate selection here could never be sent.
  if (state.manualCollection) {
    return null;
  }

  if (
    ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) ||
    (!state.newLandingPage && !state.newProductCollection && !state.manualCollection && !state.storeSpotlight)
  ) {
    if (
      (!state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.storeLandingPage) ||
      (state.adType === AD_TYPES.SPONSORED_BRAND.value &&
        (!state.newLandingPage || ((state.newProductCollection || state.manualCollection) && state.page?.label))) ||
      (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.storeLandingPage) ||
      ![AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType)
    ) {
      return null;
    }
  }

  if (
    ((state.adType === AD_TYPES.SPONSORED_BRAND.value &&
      (state.newProductCollection || state.manualCollection) &&
      state.page?.label) ||
      state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) &&
    state.page
  ) {
    return null;
  }

  const onProductsChange = (key, value) => {
    const activeProducts = value.filter((p) => !p.isDisabled);
    onChange({
      landingPageProduct: activeProducts.map((p) => p.value),
    });
  };

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <div>
        <Row style={{ marginTop: review ? 0 : "2rem" }}>
          <Col md={12}>
            <ProductSelectorField
              state={state}
              stateName={"landingPageProduct"}
              handleChildFormElement={onProductsChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              isLandingPageProductSelector={true}
              review={review}
              isModal={isModal}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

const ProductSelectorField = ({
  state,
  stateName,
  errors,
  fieldsToValidate,
  handleChildFormElement,
  isCreativeProductSelector,
  isLandingPageProductSelector,
  isExclusionProductSelector,
  // Restricts the selector table to this set of product ids. `null`/omitted means "no id restriction"
  // — the table still applies its own category, search and column filters. An empty array is a real
  // restriction and matches nothing, so only pass one when the caller knows the scope is empty.
  selectableProductIds = null,
  review,
  isModal,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [categoryFilter, setCategoryFilter] = useState(state.categoryId);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const showAllProducts =
    [
      AD_TYPES.SPONSORED_BRAND_WALMART.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_DISPLAY_V2.value,
    ].includes(state.adType) && stateName !== "creativeProduct";

  const tableSubtitle =
    review ?
      isExclusionProductSelector ? t(($) => $.advertising["Excluded Products"])
      : null
    : isCreativeProductSelector ?
      state.id ?
        t(($) => $.advertising["Creative Products"])
      : t(($) => $.advertising["Select Creative Products"])
    : isLandingPageProductSelector ?
      state.id ?
        t(($) => $.advertising["Landing Page Products"])
      : t(($) => $.advertising["Select Landing Page Products"])
    : isExclusionProductSelector ?
      state.id ?
        t(($) => $.advertising["Excluded Products"])
      : t(($) => $.advertising["Select Products to Exclude (Optional)"])
    : state.id ? t(($) => $.advertising["Selected Products"])
    : t(($) => $.advertising["Select Products to Advertise"]);

  return (
    <div className={css({ paddingRight: "xlarge" })}>
      {!showAllProducts && !review && stateName !== "creativeProduct" ?
        <CategoryField
          state={{ ...state, categoryId: categoryFilter }}
          dashboard={true}
          onChange={(value) => {
            setCategoryFilter(value.categoryId);
          }}
          additionalProductSelectionMode={state.id && !review}
          errors={errors}
          review={false}
          channel={channel}
          marketPlace={marketPlace}
          showAllCategoriesOption
          labelText={t(($) => $.advertising["Filter Products By Category"])}
          isModal={isModal}
        />
      : null}
      {tableSubtitle && <h4 className={css({ fontWeight: "bolder", fontSize: "large" })}>{tableSubtitle}</h4>}
      {errors?.product && fieldsToValidate?.includes("product") && (
        <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
          {errors?.product}
        </small>
      )}
      {errors?.landingPageProduct &&
        fieldsToValidate?.includes("landingPageProduct") &&
        isLandingPageProductSelector && (
          <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
            {errors?.landingPageProduct}
          </small>
        )}
      {errors?.creativeProduct && fieldsToValidate?.includes("creativeProduct") && isCreativeProductSelector && (
        <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
          {errors?.creativeProduct}
        </small>
      )}
      {errors?.excludedProducts && fieldsToValidate?.includes("excludedProducts") && isExclusionProductSelector && (
        <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
          {errors?.excludedProducts}
        </small>
      )}
      {(state.categoryId && stateName === "product") || stateName !== "product" ?
        <ProductSelector
          selectedRowIds={state[stateName]}
          onSelectionChange={(productDetails) => {
            // TODO: Just use ID once legacy product selector is removed
            handleChildFormElement(
              stateName,
              productDetails.map((productId) => ({ value: productId })),
            );
          }}
          context={`${stateName}mediaPlanProductSelector`}
          additionalFilters={{
            categoryId: !showAllProducts && !review && stateName !== "creativeProduct" ? categoryFilter : null,
            // `null` is the only way to say "no id restriction" — an empty array restricts the table
            // to nothing, which is what an unresolved or genuinely empty selection should show.
            productIds:
              review ? (state[stateName] ?? [])
              : stateName !== "product" && !showAllProducts ? (selectableProductIds?.map((id) => id.toString()) ?? null)
              : null,
            primaryVariant:
              (
                [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
                  state.adType,
                )
              ) ?
                true
              : null,
          }}
          hideClearAll={state.id || review}
          hideSelectedFilter={review}
          enableRowSelection={
            review ? false : (
              (row) => {
                return !state.savedAdPlan?.product?.includes(row.original.product_id);
              }
            )
          }
        />
      : null}
    </div>
  );
};

export const Creative2ProductsField = ({ state, onChange, errors, fieldsToValidate, adId, review, isModal }) => {
  const storePageProductsQuery = useGetStorePageProducts(state.page?.value, {
    select: (res) => res.products.map((p) => ({ ...p, value: p.id })),
  });
  const storePageProductOptions = storePageProductsQuery.data ?? [];
  const renderStorePageSelector =
    (state.newProductCollection || state.manualCollection || state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) &&
    state.page &&
    !state.id &&
    !review;

  const showCreative =
    (state.adType === AD_TYPES.SPONSORED_BRAND.value &&
      (state.newLandingPage || state.newProductCollection || state.manualCollection)) ||
    (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.storeLandingPage);

  if (!showCreative || state.storeSpotlight || state.autoCollection) {
    return null;
  }

  // Manual Collection collects no landing-page products to narrow to, so its creative products
  // come straight from the catalog.
  const manualCollectionNewLandingPage = state.manualCollection && !state.page;

  const usesLandingPageProducts =
    !manualCollectionNewLandingPage &&
    (!state.page ||
      !(state.newProductCollection || state.manualCollection || state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value));

  if (usesLandingPageProducts && !state.landingPageProduct?.length) {
    return null;
  }

  // With a store page chosen the page's own products are the universe; otherwise creative products
  // must come from the new landing page's selection, which is already in form state. `null` leaves
  // the selector unrestricted.
  const selectableProductIds =
    manualCollectionNewLandingPage ? null
    : usesLandingPageProducts ? (state.landingPageProduct ?? [])
    : storePageProductOptions.map((p) => p.value);

  // While store page products load, fall through when the store page selector renders — it shows
  // its own skeleton. The other paths have none, so stand in for the selector rather than
  // collapsing the section to nothing.
  if (
    (state.newProductCollection || state.manualCollection || state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) &&
    state.page &&
    storePageProductsQuery.isLoading &&
    !renderStorePageSelector
  ) {
    return (
      <div style={{ width: adId ? "100%" : "inherit" }}>
        <Row style={{ marginTop: review ? 0 : "2rem" }}>
          <Col md={12}>
            <Skeleton height={"24px"} count={5} borderRadius={"9999px"} />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <Row style={{ marginTop: review ? 0 : "2rem" }}>
        <Col md={12}>
          {renderStorePageSelector && errors?.creativeProduct && fieldsToValidate?.includes("creativeProduct") && (
            <small className="text-danger" style={{ paddingLeft: ".8rem" }}>
              {errors?.creativeProduct}
            </small>
          )}
          {renderStorePageSelector ?
            <StorePageProductSelector
              products={storePageProductOptions}
              selectedProductIds={state.creativeProduct}
              multi={true}
              isDisabled={state.id !== undefined || review}
              onSelectionChange={(selectedIds) => {
                onChange({
                  creativeProduct: selectedIds.map((id) => parseInt(id)),
                });
              }}
              isLoading={storePageProductsQuery.isLoading}
              isReview={review}
            />
          : <ProductSelectorField
              state={state}
              stateName={"creativeProduct"}
              handleChildFormElement={(key, value) => {
                onChange({
                  creativeProduct: value.map((p) => p.value),
                });
              }}
              selectableProductIds={selectableProductIds}
              isCreativeProductSelector={true}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              review={review}
              isModal={isModal}
            />
          }
        </Col>
      </Row>
    </div>
  );
};

const StoreSubpageProductsField = ({
  state,
  onChange,
  adId,
  review,
  storeSpotlightSubpageIndex,
  setActiveSubpageIndex,
  preselectedProduct,
}) => {
  const { t } = useTranslation(["advertising"]);
  const pageUrl = state.storeSpotlightSubpages[storeSpotlightSubpageIndex]?.url;
  // These come back as this org's own products, ids and asins included, so there is nothing to
  // cross-check against the catalog and no need to look an asin up elsewhere.
  const storePageProductsQuery = useGetStorePageProducts(pageUrl, {
    select: (res) => res.products.map((p) => ({ ...p, value: p.id })),
  });
  const productOptions = storePageProductsQuery.data ?? [];

  return (
    <div style={{ width: adId ? "100%" : "inherit" }}>
      <Row style={{ marginTop: review ? 0 : "2rem" }}>
        <Col
          md={12}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {storePageProductsQuery.isLoading || productOptions.length ?
            <StorePageProductSelector
              products={productOptions}
              selectedProductIds={preselectedProduct ? [preselectedProduct] : []}
              multi={false}
              isDisabled={adId !== undefined || state.id !== undefined || review}
              onSelectionChange={(selectedIds) => {
                const updatedSubpage = { ...state.storeSpotlightSubpages[storeSpotlightSubpageIndex] };
                const originalProduct = updatedSubpage.asin;

                if (!selectedIds.length) {
                  updatedSubpage.asin = "";
                  updatedSubpage.productId = null;
                } else {
                  updatedSubpage.asin = productOptions.find((p) => p.value === parseInt(selectedIds[0]))?.asin ?? "";
                  updatedSubpage.productId = selectedIds[0];
                }
                const updatedSubpages = [...state.storeSpotlightSubpages];
                updatedSubpages[storeSpotlightSubpageIndex] = updatedSubpage;
                onChange({ storeSpotlightSubpages: updatedSubpages });

                if (updatedSubpage.asin && originalProduct !== updatedSubpage.asin) {
                  setActiveSubpageIndex(null);
                }
              }}
              isLoading={storePageProductsQuery.isLoading}
              isReview={review}
            />
          : !storePageProductsQuery.isLoading ?
            <div className="d-flex justify-content-center">
              <p className="fs-5 ps-1">
                {t(($) => $.advertising["No products found in this category for the selected landing page"])}
              </p>
            </div>
          : null}
        </Col>
      </Row>
    </div>
  );
};

export const AgreeToArchiveField = ({ state, apiStatus, agreeToArchive, setAgreeToArchive, user }) => {
  const { t } = useTranslation(["advertising"]);
  if (state.advStatus === AD_STATES.ARCHIVE.value && user.organization_admin && apiStatus !== 0) {
    return (
      <Row key="RowPausedAdvMessage" className="text-start" style={{ marginBottom: "1rem", marginLeft: ".5rem" }}>
        {t(($) => $.advertising["Only a paused advertisement can be archived."])}
      </Row>
    );
  }

  if (state.advStatus === AD_STATES.ARCHIVE.value && user.organization_admin) {
    return (
      <Row key="RowArchiveCheckbox" className="text-start" style={{ marginBottom: "1rem", marginLeft: ".5rem" }}>
        <Checkbox
          label={t(($) => $.advertising["I am aware that this action cannot be undone."])}
          value={agreeToArchive}
          onChange={() => {
            setAgreeToArchive(!agreeToArchive);
          }}
        />
      </Row>
    );
  }
  return null;
};

export const AdvancedTargettingButton = ({ show, onChange, skipToAdvancedMenu, disabled }) => {
  const { t } = useTranslation(["advertising"]);
  if (show === false) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "2rem",
      }}
    >
      <p
        style={{
          textAlign: "center",
          color: "#6C757D",
          fontSize: "1.5rem",
          fontWeight: 500,
          paddingTop: "1rem",
          margin: "1rem auto 2rem",
          maxWidth: "90%",
        }}
      >
        {t(
          ($) =>
            $.advertising[
              "We pre-selected some of your settings based on your media plan and targeting types. If you want to make changes, click the link below. Only recommended for experienced users."
            ],
        )}
      </p>
      <Button
        variant="link"
        onPress={() => {
          onChange({
            renderAdvancedOptions: true,
          });
          skipToAdvancedMenu();
        }}
        isDisabled={disabled}
        className={css({
          margin: "auto",
          marginBottom: "4rem",
        })}
      >
        {t(($) => $.advertising["Advanced Settings"])}
      </Button>
    </div>
  );
};

const StoreSpotlightSubpageField = ({
  state,
  onChange,
  review,
  adId,
  pageOptions,
  subpageIndex,
  activeSubpageIndex,
  setActiveSubpageIndex,
  index,
}) => {
  const { t } = useTranslation(["advertising"]);
  const subpage = state.storeSpotlightSubpages[subpageIndex];
  // Same query key as the selector below, so this resolves the chosen product from cache rather than
  // needing the org's whole catalogue to look it up by asin.
  const storePageProductsQuery = useGetStorePageProducts(subpage?.url, {
    select: (res) => res.products.map((p) => ({ ...p, value: p.id })),
  });

  if (!state.storeSpotlight) {
    return null;
  }

  const reviewState = adId || state.id || review;

  const product = storePageProductsQuery.data?.find((p) =>
    subpage?.productId != null ? p.value === parseInt(subpage.productId) : p.asin === subpage?.asin,
  );

  const availablePageOptions = pageOptions.filter(
    (page) =>
      !state.storeSpotlightSubpages.map((subpage) => subpage?.url)?.includes(page.value) &&
      !page.label.startsWith("Home") &&
      page.label !== state.page?.label,
  );

  return (
    <Row style={{ marginBottom: "1rem" }} className={"my-3"}>
      <Col
        md={6}
        className={"pe-5"}
        hidden={!state.storeSpotlight}
        onFocus={() => {
          if (!activeSubpageIndex === subpageIndex) {
            setActiveSubpageIndex(subpageIndex);
          }
        }}
      >
        <FormGroupTile
          type={reviewState ? "formcontrol" : "select"}
          stateName={"page"}
          key={index}
          handleChildFormElement={(key, value) => {
            const updatedSubpage = { ...state.storeSpotlightSubpages[subpageIndex] };
            updatedSubpage.url = value.value;
            updatedSubpage.pageTitle = value.label;
            updatedSubpage.asin = "";
            updatedSubpage.productId = null;

            const updatedSubpages = [...state.storeSpotlightSubpages];
            updatedSubpages[subpageIndex] = updatedSubpage;

            onChange({ storeSpotlightSubpages: updatedSubpages });

            if (subpageIndex !== activeSubpageIndex) {
              setTimeout(() => {
                setActiveSubpageIndex(subpageIndex);
              });
            }
          }}
          label={
            reviewState || state.storeSpotlightSubpages[subpageIndex]?.pageTitle ?
              reviewState ?
                t(($) => $.advertising["Store Page #{index}"], { index: subpageIndex + 1 })
              : t(($) => $.advertising["Store Page"])
            : t(($) => $.advertising["Choose a Page"])
          }
          labelClassName={"fs-4"}
          disabled={reviewState}
          defaultValue={
            reviewState ?
              state.storeSpotlightSubpages[subpageIndex].pageTitle
            : {
                value: state.storeSpotlightSubpages[subpageIndex].url,
                label: state.storeSpotlightSubpages[subpageIndex].pageTitle,
              }
          }
          options={availablePageOptions}
          allowEventBubbling={true}
        />
      </Col>
      <Col
        md={6}
        className={"pe-5"}
        hidden={!state.storeSpotlightSubpages[subpageIndex]?.asin && activeSubpageIndex !== subpageIndex}
      >
        {product && (
          <>
            <ControlLabel>
              <h6
                style={{
                  color: "rgb(115, 112, 110)",
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "15px",
                }}
              >
                {t(($) => $.advertising["Selected ASIN"])}
              </h6>
            </ControlLabel>
            <ProductLabel product={product} />
          </>
        )}
      </Col>

      {state.storeSpotlightSubpages[subpageIndex].pageTitle && activeSubpageIndex === subpageIndex && (
        <StoreSubpageProductsField
          state={state}
          onChange={onChange}
          adId={adId}
          review={review}
          storeSpotlightSubpageIndex={subpageIndex}
          setActiveSubpageIndex={setActiveSubpageIndex}
          preselectedProduct={state.storeSpotlightSubpages[subpageIndex]?.productId}
        />
      )}
    </Row>
  );
};

export const StoreSpotlightSubpageDialogue = ({
  state,
  onChange,
  review,
  adId,
  pageOptions,
  errors,
  fieldsToValidate,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [activeSubpageIndex, setActiveSubpageIndex] = useState(null);

  if (!state.storeSpotlight) {
    return null;
  }

  const reviewState = adId || state.id || review;

  return (
    <Row style={{ marginBottom: "1rem" }} className={reviewState ? "mt-0" : "my-5"}>
      <h2
        style={{
          fontWeight: 700,
          marginTop: 0,
          fontSize: "24px",
        }}
        hidden={reviewState}
      >
        {t(($) => $.advertising["Store Spotlight Subpages"])}
      </h2>
      {state.store ?
        <>
          <p
            style={{
              fontSize: "16px",
              maxWidth: "90%",
              marginBottom: ".5rem",
            }}
            translate="yes"
            hidden={review}
          >
            {t(($) => $.advertising["Setup 3 store subpages below to showcase your Amazon Store."])}
          </p>
          <div className="mb-3">
            {errors.storeSpotlightSubpages && fieldsToValidate?.includes("storeSpotlightSubpages") && (
              <small className="text-danger">{errors.storeSpotlightSubpages}</small>
            )}
          </div>

          {state.storeSpotlightSubpages?.map((subpage, index) => (
            <div
              key={index}
              onClick={() => {
                setActiveSubpageIndex(index);
              }}
            >
              <div className="d-flex">
                <h3 className="pe-5" style={{ fontWeight: 500, marginTop: 0, fontSize: "16px" }} hidden={reviewState}>
                  {t(($) => $.advertising["Store Page #{index}"], { index: index + 1 })}{" "}
                </h3>
                {!reviewState && (
                  <CustomHref
                    text={
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveSubpageIndex(activeSubpageIndex === index ? null : index);
                        }}
                      >
                        {activeSubpageIndex === index ?
                          t(($) => $.advertising["Collapse"])
                        : t(($) => $.advertising["Edit"])}
                      </span>
                    }
                  />
                )}
              </div>
              <StoreSpotlightSubpageField
                state={state}
                onChange={onChange}
                adId={adId}
                review={review}
                subpageIndex={index}
                pageOptions={pageOptions}
                activeSubpageIndex={activeSubpageIndex}
                setActiveSubpageIndex={setActiveSubpageIndex}
                index={index}
              />
              <hr className="mx-3 my-4" />
            </div>
          ))}
        </>
      : <p
          style={{
            fontSize: "16px",
            maxWidth: "90%",
            marginBottom: "2rem",
          }}
          translate="yes"
        >
          {t(($) => $.advertising["Choose a store to start building out subpages."])}
        </p>
      }
    </Row>
  );
};
